import type {
  SafeStorage,
  SafeStorageReadResult,
} from "@/features/settings/stores/safeStorage";

export interface LegacyDisplaySettings {
  darkMode?: boolean;
  privacyMode?: boolean;
}

type LegacyStorage = Pick<SafeStorage, "getItem"> &
  Partial<Pick<SafeStorage, "getItemWithStatus">>;

export function readLegacyDisplaySettings(
  storage: LegacyStorage,
): LegacyDisplaySettings | null {
  const current = readStorageItem(storage, "nijiurachan-settings");
  if (current.failed || current.value !== null) return null;

  const settings: LegacyDisplaySettings = {};
  const theme = readStorageItem(storage, "futaba_theme").value;
  if (theme === "dark") settings.darkMode = true;
  if (theme === "light") settings.darkMode = false;

  const privacy = readStorageItem(storage, "futaba_privacy_mode").value;
  if (privacy === "1") settings.privacyMode = true;
  if (privacy === "0") settings.privacyMode = false;

  return settings;
}

function readStorageItem(
  storage: LegacyStorage,
  key: string,
): SafeStorageReadResult {
  return (
    storage.getItemWithStatus?.(key) ?? {
      value: storage.getItem(key),
      failed: false,
    }
  );
}

/**
 * 表示テーマの保存値。
 * - `true`: ダーク固定（ユーザーが明示的に選択）
 * - `false`: ライト固定（ユーザーが明示的に選択）
 * - `"system"`: OSの`prefers-color-scheme`に追従（ユーザーが明示的に選択）
 * - `null`: 未設定（後方互換のため、既定はダークとして扱う）
 */
export type DarkModeSetting = boolean | "system" | null;

/**
 * 設定値から実効ライトモードを解決する。
 * `darkMode`が`"system"`の場合のみ`systemPrefersDark`（OSの現在値）を参照する。
 * それ以外（`true`/`false`/`null`）は常に設定値のみで解決し、OS設定は無視する
 * （既存ユーザーの手動選択・未設定=ダークという挙動を変えないため）。
 */
export function resolveLightMode(
  darkMode: DarkModeSetting,
  systemPrefersDark: boolean = true,
): boolean {
  if (darkMode === "system") return !systemPrefersDark;
  return darkMode === false;
}

/** テーマ選択UI（Select）の表示値。保存値(DarkModeSetting)とは別の型で扱う */
export type ThemeSelectValue = "dark" | "light" | "system";

/** 保存値(darkMode)から選択UIの表示値へ変換。未設定(null)は従来どおりダーク扱い */
export function darkModeToThemeSelectValue(
  darkMode: DarkModeSetting,
): ThemeSelectValue {
  if (darkMode === "system") return "system";
  if (darkMode === false) return "light";
  return "dark";
}

/** 選択UIの表示値から保存値(darkMode)へ変換 */
export function themeSelectValueToDarkMode(value: string): DarkModeSetting {
  if (value === "light") return false;
  if (value === "system") return "system";
  return true;
}
